import { Link } from 'react-router-dom';
import { ArrowRight, Maximize2 } from 'lucide-react';
import type { AnnouncementDto, AnnouncementType } from '@mesterplan/api-client';
import { cn } from '@/lib/utils';
import { resolveImageUrl } from './resolveImageUrl';
import { ANNOUNCEMENT_TYPE_LABEL, formatShortDate, isExternalAction, platformTags } from './whatsNewFormat';

/** Type pills use the readable counterparts of the pale tokens (mockup: .pill.feat / .imp / .fix). */
const TYPE_PILL: Record<AnnouncementType, string> = {
  feature: 'bg-primary-50 text-primary dark:bg-primary-900/30 dark:text-primary-400',
  improvement: 'bg-success-light text-[#0A7D69] dark:bg-success/15 dark:text-[#4AEBD2]',
  fix: 'bg-warning-light text-[#94590A] dark:bg-warning/15 dark:text-[#FFBE4D]',
};

export interface WhatsNewNoteCardProps {
  item: AnnouncementDto;
  /** Show the "Ny" marker on unread notes (the feed page). */
  showUnread?: boolean;
  /** Show the date in the meta row (the feed page). */
  showDate?: boolean;
  /** Tighter, chrome-less row for the launch dialog: one bullet, no action, a small screenshot. */
  compact?: boolean;
  onZoom?: (item: AnnouncementDto) => void;
}

/**
 * One note: type pill, platform tags, title, bullets, "Prøv det" and a 16:10 screenshot.
 * Text left and screenshot right from 768 px, stacked below (mockup, 375 px). The screenshot is
 * `object-contain` so a wrong aspect ratio never crops the product.
 */
export function WhatsNewNoteCard({ item, showUnread = false, showDate = false, compact = false, onZoom }: WhatsNewNoteCardProps) {
  const tags = platformTags(item);
  const imageUrl = item.image ? resolveImageUrl(item.image.url) : null;
  const bullets = compact ? item.summary.slice(0, 1) : item.summary;

  return (
    <article
      data-testid="whats-new-note"
      className={cn(
        'grid items-start gap-3.5',
        compact
          ? 'border-t border-bordergray py-3.5 first:border-t-0 first:pt-0 dark:border-dark-border'
          : 'grid-cols-1 rounded-md bg-white p-4 shadow-card dark:border dark:border-dark-border dark:bg-dark-card dark:shadow-none',
        compact && item.image && 'grid-cols-[minmax(0,1fr)_132px]',
        !compact && item.image && 'md:grid-cols-[minmax(0,1fr)_300px] md:gap-6 md:px-6 md:py-5',
      )}
    >
      <div className="min-w-0">
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          {showUnread && !item.isRead && (
            <span data-testid="whats-new-unread" className="inline-flex h-[22px] items-center rounded-full bg-primary px-[9px] text-[11px] font-semibold text-white">
              Ny
            </span>
          )}
          <span className={cn('inline-flex h-[22px] items-center rounded-full px-[9px] text-[11px] font-semibold', TYPE_PILL[item.type])}>
            {ANNOUNCEMENT_TYPE_LABEL[item.type]}
          </span>
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex h-[22px] items-center rounded-full border border-bordergray px-[9px] text-[11px] font-semibold text-muted dark:border-dark-border dark:text-dark-muted"
            >
              {tag}
            </span>
          ))}
          {showDate && <span className="ml-auto text-[12px] text-muted dark:text-dark-muted">{formatShortDate(item.publishedAt)}</span>}
        </div>
        <h3 className={cn('font-semibold text-dark dark:text-dark-text', compact ? 'mb-0.5 text-[14px] leading-[22px]' : 'mb-1.5 text-[15px] leading-[22px]')}>{item.title}</h3>
        <ul className={cn('grid list-disc gap-[3px] pl-[18px] text-muted dark:text-dark-muted', compact ? 'text-[13px] leading-[19px]' : 'text-[14px] leading-[21px]')}>
          {bullets.map((bullet, index) => (
            <li key={index}>{bullet}</li>
          ))}
        </ul>
        {!compact && item.actionUrl && item.actionLabel && (
          <ActionLink url={item.actionUrl} label={item.actionLabel} />
        )}
      </div>
      {item.image && imageUrl && (
        <button
          type="button"
          data-testid="whats-new-shot"
          onClick={() => onZoom?.(item)}
          disabled={!onZoom}
          aria-label={`Forstør skærmbillede: ${item.image.alt}`}
          className="group relative aspect-[16/10] w-full cursor-zoom-in overflow-hidden rounded-md border border-bordergray bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 dark:border-dark-border dark:bg-dark-surface disabled:cursor-default"
        >
          <img src={imageUrl} alt={item.image.alt} loading="lazy" className="h-full w-full object-contain" />
          {onZoom && (
            <span className="absolute bottom-1.5 right-1.5 flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-white text-muted shadow-sm dark:bg-dark-card dark:text-dark-muted">
              <Maximize2 className="h-[15px] w-[15px]" aria-hidden="true" />
            </span>
          )}
        </button>
      )}
    </article>
  );
}

function ActionLink({ url, label }: { url: string; label: string }) {
  if (isExternalAction(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={actionClass} data-testid="whats-new-action">
        {label}
        <ArrowRight className="h-4 w-4" />
      </a>
    );
  }
  return (
    <Link to={url} className={actionClass} data-testid="whats-new-action">
      {label}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
}

const actionClass =
  'mt-2.5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary hover:underline dark:text-primary-400';
